/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {useIsPhone} from '../../../hooks/useMedia';
import {FilterDrawerCollapseAllIcon} from '../../../icons/current/FilterDrawerCollapseAllIcon';
import {FilterDrawerExpandAllIcon} from '../../../icons/current/FilterDrawerExpandAllIcon';
import Render from '../../../render/Render';
import {dlsColor} from '../../../theme/theme';
import {CloseButton} from '../../buttons/CloseButton';
import Tooltip from '../../tooltip/Tooltip';

export interface DrawerHeaderActionsProps {
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
}

const useStyles = makeStyles(() => ({
  actions : {
    flexShrink     : 0,
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'flex-end',
    alignItems     : 'center',
    gap            : '8px',
    padding        : '0 8px'
  },
  // phone (Figma mobile grid, 16px margin / 4 columns): no gap between the
  // two 44px hit areas and a 6px right padding, so the group fits the last
  // column and the close button's 24px box still ends at the 16px margin
  // (6 + its 10px hit-area inset) - leaving more room for the breadcrumb ...
  actionsPhone : {
    gap     : 0,
    padding : '0 6px 0 0'
  },
  expandButton : {
    boxSizing      : 'border-box',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '44px',
    height         : '44px',
    padding        : 0,
    border         : 0,
    background     : 'transparent',
    cursor         : 'pointer'
  }
}));

/**
 * DrawerHeaderActions renders the Drawer Header's right-side icon cluster:
 * the expand/collapse-list toggle and the `CloseButton`. Per Details.txt
 * ("Expand / Collapse icon behavior: Follow the same behavior rules as the
 * Filter drawer"), the toggle mirrors `FilterDrawerHeader`'s own
 * expand/collapse-all control exactly - a controlled boolean + callback,
 * `FilterDrawerExpandAllIcon`/`FilterDrawerCollapseAllIcon` swapped via
 * `Render.If`/`Render.Else`, and a tooltip surfacing the label on hover -
 * rather than the unrelated `useExpandCollapse` hook (which persists the
 * side navigation's collapsed-width preference, a different concern).
 *
 * ```typescript
 *
 *   import {DrawerHeaderActions} from
 *     '@ion/dls/src/components/drawer/current/DrawerHeaderActions';
 *   ...
 *
 *   <DrawerHeaderActions isExpanded={isExpanded}
 *     onToggleExpand={onToggleExpand}
 *     onClose={onClose} />
 *
 * ```
 *
 */
export const DrawerHeaderActions = memo(({
  isExpanded,
  onToggleExpand,
  onClose
}: DrawerHeaderActionsProps) => {
  const classes = useStyles();
  const isPhone = useIsPhone();

  const onToggle = useCallback(function() {
    onToggleExpand();
  }, [onToggleExpand]);

  const expandLabel = isExpanded
    ? translate('filters:drawer.collapseAll')
    : translate('filters:drawer.expandAll');

  return (
    <div className={classnames(classes.actions, {[classes.actionsPhone]: isPhone})}>
      <Tooltip title={expandLabel} arrow>
        <button type='button'
          className={classes.expandButton}
          onClick={onToggle}
          aria-label={expandLabel}>
          <Render>
            <Render.If condition={isExpanded}>
              <FilterDrawerCollapseAllIcon color={dlsColor('grey1')} />
            </Render.If>
            <Render.Else>
              <FilterDrawerExpandAllIcon color={dlsColor('grey1')} />
            </Render.Else>
          </Render>
        </button>
      </Tooltip>
      <CloseButton onClick={onClose} />
    </div>
  );
});

export default DrawerHeaderActions;
